import styled, { css } from 'styled-components';
import { ScButton, ScButtonWrapper } from '@/components/buttons/Button/styled';
import { getCDNImageUrl } from '@/helpers/cdn';
import type { RegistrationConfig } from '@/modules/registration/helpers/types';
import { device, hexToRGB, typography } from '@/styles/helpers';

const prizeIconUrl = getCDNImageUrl('/common/prizeIcon.svg');

interface ScRegistrationProps {
  styleScheme?: RegistrationConfig['styleScheme'];
}

export const ScRegistration = styled.div`
  position: relative;
  width: 100%;
  margin: 8px auto;
  justify-content: flex-start;
  align-items: center;
  display: grid;
  grid-template-columns: 1fr;
  max-width: 480px;

  @media (${device.mobile}) {
    padding: 16px 8px 62px;
  }
`;

export const ScRegistrationContent = styled.div<ScRegistrationProps>`
  width: 100%;
  flex: auto;
  color: ${({ theme, styleScheme }) => styleScheme?.color || theme.blackColor};
  background: ${({ theme, styleScheme }) =>
    styleScheme?.bg || theme.whiteColor};
  border-radius: 24px;
  padding: 32px 32px 0;
  position: relative;

  @media (${device.tablet}) {
    max-width: 900px;
  }

  ${({ styleScheme }) =>
    styleScheme?.contentPaddingTop &&
    css`
      padding-top: ${styleScheme.contentPaddingTop};
    `}

  @media (${device.mobile}) {
    width: 100%;
    margin: 0;
    padding: 32px 16px 0;

    ${({ styleScheme }) =>
      styleScheme?.contentPaddingTop &&
      css`
        padding-top: ${styleScheme.contentPaddingTop};
      `}
  }
`;

export const ScRegistrationWrapButtons = styled.div<{
  cols?: number;
  isSticky?: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols || 2}, 1fr)`};
  gap: 8px;
  align-items: center;
  margin-bottom: 24px;

  ${({ isSticky }) =>
    isSticky &&
    css`
      position: sticky;
      bottom: 8px;
      left: 0;
      z-index: 1;
    `}

  .left-col,
  .right-col {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

export const ScRegistrationInfo = styled.div<ScRegistrationProps>`
  display: flex;
  justify-content: center;
  gap: 5px;
  text-align: center;

  ${ScButtonWrapper} {
    .text {
      text-transform: none;
    }
    gap: 7px;
  }

  margin: 32px -32px 0 -32px;
  padding: 12px;
  font-size: 16px;
  line-height: 24px;
  background-color: ${({ theme, styleScheme }) =>
    styleScheme?.footer?.bg || theme.gray1Color};
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  color: ${({ theme, styleScheme }) => styleScheme?.color || theme.blackColor};

  ${ScButton} {
    min-width: auto;
    padding: 0 2px 0 0;
    overflow: visible;

    & > .text {
      font-weight: 700;
      font-size: 16px;
      line-height: 24px;
      text-transform: none;

      ${({ styleScheme }) =>
        styleScheme?.footer?.fontColor &&
        css`
          & {
            color: ${styleScheme?.footer?.fontColor};
          }
        `}
    }

    .awareWrap {
      display: none;
    }

    ${({ styleScheme }) =>
      styleScheme?.color &&
      css`
        & + i {
          color: ${styleScheme?.footer?.fontColor};
        }

        ${
          styleScheme?.footer?.fontColor &&
          css`
          @media (hover: hover) {
            &:hover {
              & > .text {
                color: ${hexToRGB(styleScheme?.footer.fontColor, 0.8)};
              }
              & + i {
                color: ${hexToRGB(styleScheme?.footer.fontColor, 0.8)};
              }
            }
          }
        `
        }
      `}
  }

  @media (${device.mobile}) {
    margin: 32px -16px 0 -16px;
    background-color: ${({ theme, styleScheme }) =>
      styleScheme?.footer?.mobileBg ||
      styleScheme?.footer?.bg ||
      theme.gray1Color};
  }
`;

interface ScFormNavigationProps {
  withMarginBottom: boolean;
  styleScheme?: RegistrationConfig['styleScheme'];
}

export const ScFormNavigation = styled.div<ScFormNavigationProps>`
  position: relative;
  z-index: 0;
  margin-bottom: ${({ withMarginBottom }) => withMarginBottom && '32px'};

  & > ul {
    list-style: none;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;

    & > li {
      position: relative;
      margin: 0 0 0 12px;

      &:not(:last-child) {
        &:before {
          content: "";
          display: block;
          width: 40px;
          height: 12px;
          background-color: ${({ theme, styleScheme }) =>
            styleScheme?.steps?.bg || theme.gray1Color};
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          left: 6px;
        }
      }
    }
    & > li:not(:last-child),
    li:not(:first-child) {
      margin: 0 10px;
    }
  }
`;

export enum StatusItem {
  ACTIVE = 1,
  COMPLETED = 2,
  NOT_ACTIVE = 3,
  FINISH = 4,
}

interface ScFormNavigationItemProps {
  status: StatusItem;
  styleScheme?: RegistrationConfig['styleScheme'];
}

export const ScFormNavigationItem = styled.div<ScFormNavigationItemProps>`
  position: relative;
  z-index: 1;
  background-color: ${({ theme, styleScheme }) =>
    styleScheme?.steps?.bg || theme.gray1Color};
  color: ${({ theme, styleScheme }) =>
    styleScheme?.steps?.color || theme.whiteColor};
  width: ${({ status }) => (status === StatusItem.FINISH ? '30px' : '20px')};
  height: ${({ status }) => (status === StatusItem.FINISH ? '30px' : '20px')};
  border-radius: 50%;

  &::after {
    content: "";
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    width: 16px;
    height: 16px;

    ${({ status, theme, styleScheme }) => {
      switch (status) {
        case StatusItem.ACTIVE:
        case StatusItem.COMPLETED:
          return css`
            background-color: ${styleScheme?.steps?.active || theme.mainColor};
          `;
        case StatusItem.NOT_ACTIVE:
          return css`
            background-color: ${
              styleScheme?.steps?.inactive || theme.grayMegaLightColor
            };
          `;
        case StatusItem.FINISH:
          return css`
            width: 26px;
            height: 26px;
            background-color: ${styleScheme?.steps?.active || theme.mainColor};
            background-image: url(${prizeIconUrl});
            background-size: 16px 15px;
            background-position: center;
            background-repeat: no-repeat;
            z-index: 1;
          `;
        default:
          return '';
      }
    }}
  }

  .step {
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    align-items: center;
    transform: translate(-50%, -50%);
    font-weight: 700;
    font-size: 10px;
    line-height: 14px;
    z-index: 2;
  }
`;

export const ScRegistrationBanner = styled.div<{
  styleScheme?: RegistrationConfig['styleScheme'];
  titleColor?: string;
  offerColor?: string;
}>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: ${({ styleScheme }) =>
    styleScheme?.regBanner?.marginBottom || '32px'};
  overflow: hidden;
  border-radius: 16px;
  z-index: 1;

  & > .bg {
    object-fit: cover;
    object-position: top center;
    z-index: -1;
  }

  & > .offer-container {
    display: flex;
    padding: 16px 0 16px 16px;
    flex-direction: column;
    justify-content: center;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.16px;
    color: ${({ theme, styleScheme, offerColor }) =>
      offerColor || styleScheme?.regBanner?.offerColor || theme.whiteColor};

    & > h2 {
      font-size: inherit;
      letter-spacing: inherit;
      line-height: inherit;
      align-self: flex-start;
      color: ${({ theme, styleScheme, titleColor }) =>
        titleColor ||
        styleScheme?.regBanner?.titleColor ||
        theme.secondaryColor};
      margin-bottom: 8px;
    }

    & > .offer-1 {
      font-size: 22px;
      line-height: 28px;
      letter-spacing: 0.44px;
      white-space: pre-line;
      text-transform: none;

      @media (${device.mobile}) {
        font-size: 20px;
        line-height: 26px;
        letter-spacing: 0.4px;
      }

      @media (max-width: 379px) {
        font-size: inherit;
        line-height: inherit;
        letter-spacing: inherit;
      }
    }
  }
`;

export const ScPromoCheckboxesContainer = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  margin-left: 32px;
  gap: 12px;
`;

export const ScChangeCountryMessage = styled.div<{
  styleScheme?: RegistrationConfig['styleScheme'];
}>`
  display: flex;
  align-items: center;
  margin-top: 4px;

  .tooltip-icon {
    font-size: 24px;
    color: ${({ theme }) => theme.blackColor};

    ${({ styleScheme }) =>
      styleScheme?.countryTooltip?.color &&
      css`
        color: ${styleScheme.countryTooltip.color};
      `}
  }

  .message {
    ${typography['caption1regular']}
    margin-left: 4px;

    ${({ styleScheme }) =>
      styleScheme?.countryTooltip?.textColor &&
      css`
        color: ${styleScheme.countryTooltip.textColor};
      `}
  }

  .message b {
    ${typography['caption1bold']}
    color: ${({ theme }) => theme.secondaryColor};
    text-decoration: underline;
    cursor: pointer;

    ${({ styleScheme }) =>
      styleScheme?.colorLinks &&
      css`
        color: ${styleScheme.colorLinks};
      `}
  }
`;

export const ScRegistrationFormRow = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 8px;
  margin-bottom: 5px;
  width: 100%;
`;

export const ScErrorLink = styled.span`
  color: ${({ theme }) => theme.error};
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
`;

export const ScStepsReplacementWrapper = styled.div<{ withMargin?: boolean }>`
  margin-bottom: ${({ withMargin }) => (withMargin ? '32px' : 0)};
`;
